import { Add } from '@mui/icons-material';
import { Box, Typography, IconButton, Button, Tooltip } from '@mui/material';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Group } from '../../../RaptorJobValidationSchema';
import { CrewSelector } from '../components/CrewSelector';
import { CrewField, SelectedEquipment } from '../RaptorJobCreateModal';
import { EquipmentSelector } from '../components/EquipmentSelector';
import { CreateJobGroups } from '../components/CreateJobGroups';
import { useRaptorJobResourcesStep } from '../hook/useRaptorJobResourcesStep';
import tid from '@/common/infrastructure/config/tid';

interface RaptorJobResourcesStepProps {
  crewFields: CrewField[];
  setCrewFields: (fields: CrewField[]) => void;
  selectedEquipment: SelectedEquipment | null;
  setSelectedEquipment: (equipment: SelectedEquipment | null) => void;
  selectedClass: { id?: string | number | null; name?: string | null } | null;
  setSelectedClass: (value: { id?: string | number | null; name?: string | null } | null) => void;
  selectedType: { id?: string | number | null; name?: string | null } | null;
  setSelectedType: (value: { id?: string | number | null; name?: string | null } | null) => void;
  selectedSize: { id?: string | number | null; name?: string | null } | null;
  setSelectedSize: (value: { id?: string | number | null; name?: string | null } | null) => void;
  equipmentSearchTerm: string;
  setEquipmentSearchTerm: (term: string) => void;
  onUnavailableResources?: (hasUnavailableResources: boolean) => void;
  recurrence: 'single' | 'multi';
}

export type RaptorJobResourcesFormType = {
  groups: Group[];
  startDate?: Date | null;
  endDate?: Date | null;
  recurrence: 'single' | 'multi';
};

export const RaptorJobResourcesStep = <T extends RaptorJobResourcesFormType = RaptorJobResourcesFormType>({
  crewFields,
  setCrewFields,
  selectedEquipment,
  setSelectedEquipment,
  selectedClass,
  setSelectedClass,
  selectedType,
  setSelectedType,
  selectedSize,
  setSelectedSize,
  equipmentSearchTerm,
  setEquipmentSearchTerm,
  onUnavailableResources,
  recurrence
}: RaptorJobResourcesStepProps) => {
  const { canAddGroup, handleAddCrewField, handleAddGroup, values } = useRaptorJobResourcesStep<T>({
    selectedEquipment,
    setSelectedEquipment,
    selectedClass,
    setSelectedClass,
    selectedType,
    setSelectedType,
    selectedSize,
    setSelectedSize,
    crewFields,
    setCrewFields
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <LocalShippingOutlinedIcon />
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700 }}>
          Equipment
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Requirements
        </Typography>
      </Box>
      <EquipmentSelector
        selectedClass={selectedClass}
        setSelectedClass={setSelectedClass}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        selectedSize={selectedSize}
        setSelectedSize={setSelectedSize}
        equipmentSearchTerm={equipmentSearchTerm}
        setEquipmentSearchTerm={setEquipmentSearchTerm}
        selectedEquipment={selectedEquipment}
        setSelectedEquipment={setSelectedEquipment}
        startDate={values.startDate}
        endDate={values.endDate}
        recurrence={recurrence}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700 }}>
          Crew
        </Typography>
        <IconButton onClick={handleAddCrewField} {...tid('jobs:create:add-crew-field-button')}>
          <AddCircleOutlineIcon color="primary" />
        </IconButton>
      </Box>

      <CrewSelector
        recurrence={values.recurrence}
        crewFields={crewFields}
        setCrewFields={setCrewFields}
        startDate={values.startDate}
        endDate={values.endDate}
      />
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Tooltip title={!canAddGroup ? 'Add at least one element: equipment (or filters), or a role' : ''} arrow>
          <span>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={handleAddGroup}
              disabled={!canAddGroup}
              {...tid('jobs:create:add-group-button')}
            >
              ADD GROUP
            </Button>
          </span>
        </Tooltip>
      </Box>

      <CreateJobGroups onUnavailableResources={onUnavailableResources} />
    </Box>
  );
};
